import React, { FunctionComponent } from 'react';
import {
  GetDonationPromptsQuery,
  DonationPrompt,
} from '../../../types/__generated__';
import { DonationPromptsSkeleton } from './DonationPromptsSkeleton';

import { useBasePageQuery, useOptimizely, usePageMeta } from '../../hooks';
import { useTranslation } from 'react-i18next';
import PageSection, { PageSectionProps } from '../PageSection/PageSection';
import DonateButton from '../DonateButton/DonateButton';
import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import parseCurrency from '../../utils/parseCurrency';
import { Experiments } from '../../constants/experiments';
import isDesktopViewport from '../../utils/isDesktopViewport';

export function encodeNodeId(type: any, ...parts: string[]): string {
  return Buffer.from([type, ...parts].join(':')).toString('base64');
}

const DOCUMENT = gql`
  query getDonationPrompts($id: ID!) {
    node(id: $id) {
      ... on Charity {
        id
        displayCurrency
        externalId
        donationPrompts {
          description
          amount
          type
        }
      }
    }
  }
`;

const isOneOff = (donationPrompt: DonationPrompt) =>
  donationPrompt.type === 'OneOff';

const DonationPrompts: FunctionComponent = () => {
  const { data: pageData } = useBasePageQuery();
  const { pageType } = usePageMeta();
  const { t } = useTranslation('common', { useSuspense: false });
  const optimizely = useOptimizely();

  const isOnePage = pageType === 'one_page';
  const isDesktop = typeof window !== 'undefined' ? isDesktopViewport() : false;
  const charity = pageData?.page?.relationships?.beneficiaries.nodes[0];
  const id =
    pageType === 'one_page'
      ? charity?.__typename === 'Charity' &&
        encodeNodeId('Charity', charity.externalId.toString())
      : charity?.__typename === 'Charity' && charity.id;

  const { data, error, loading, refetch } = useQuery<GetDonationPromptsQuery>(
    DOCUMENT,
    {
      variables: {
        id,
      },
      ssr: false,
    },
  );

  const pageSectionProps: PageSectionProps = {
    title: t('donationPrompts.title', 'Donation Impact'),
    hasBorder: isOnePage ? (isDesktop ? false : true) : true,
    className: '',
    hasBottomBorder: isDesktop ? true : false,
  };

  if (loading) {
    return (
      <PageSection {...{ ...pageSectionProps, titleId: 'donation-prompts1' }}>
        <DonationPromptsSkeleton animated displayRefreshButton={false} />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection {...{ ...pageSectionProps, titleId: 'donation-prompts2' }}>
        <DonationPromptsSkeleton onRefresh={refetch} />
      </PageSection>
    );
  }

  const donationPrompts =
    (data?.node?.__typename === 'Charity' &&
      data?.node?.donationPrompts?.filter(isOneOff)) ||
    [];

  const hasPrompts = Boolean(donationPrompts.length);

  const currencyCode =
    (data?.node?.__typename === 'Charity' && data?.node.displayCurrency) ||
    'GBP';

  if (!hasPrompts) {
    return null;
  }

  //enroll users only if prompts exists

  const donationPromptsDecision = optimizely?.decide(
    Experiments.ConsumerDarkslide.DonationPrompts.flagKey,
  );

  return donationPromptsDecision?.enabled ? (
    <PageSection {...{ ...pageSectionProps, titleId: 'donation-prompts3' }}>
      {donationPrompts.map(({ amount, description }: DonationPrompt, index) => (
        <DonationPromptItem
          key={`donationPrompt-${index}`}
          amount={amount!}
          description={description!}
          displayCurrency={currencyCode}
        ></DonationPromptItem>
      ))}

      <DonateButton className="jg-space-mtsm" />
    </PageSection>
  ) : null;
};

interface DonationPromptsItemProps {
  amount: number;
  description: string;
  displayCurrency: string;
}

const DonationPromptItem = ({
  amount,
  description,
  displayCurrency,
}: DonationPromptsItemProps) => {
  return (
    <div
      className={classNames(
        'jg-display-flex jg-flex-aic jg-space-psm jg-space-mvsm jg-background--white',
      )}
    >
      <div className={classNames('jg-text--bold jg-space-mrsm')}>
        <strong>
          {parseCurrency(displayCurrency, amount, {
            roundDown: true,
            wholeNumber: true,
          })}
        </strong>
      </div>
      <div>{description}</div>
    </div>
  );
};

export default DonationPrompts;
